/**
 * Referral System Helper Functions
 *
 * This module provides utilities for working with the referral system.
 * Referral codes are 8-character strings (A-Z, 2-9, excluding O/0/I/1)
 */

import { supabase } from '@/integrations/supabase/client';

export interface ReferredUser {
  id: string;
  first_name: string;
  last_name: string;
  created_at: string;
  user_type: 'client' | 'practitioner';
}

export interface ReferralStats {
  my_code: string | null;
  applied_code: string | null;
  can_apply_code: boolean;
  total_referrals: number;
  client_referrals: number;
  practitioner_referrals: number;
  days_since_registration: number;
  referred_clients?: ReferredUser[];
  referred_practitioners?: ReferredUser[];
}

export type UserType = 'client' | 'practitioner';

export interface ApplyReferralResult {
  success: boolean;
  message?: string;
  error?: string;
}

/**
 * Generate a random 8-character referral code
 * Characters: A-Z (excluding O, I) and 2-9 (excluding 0, 1)
 * This is client-side generation for preview - actual codes are generated server-side
 */
export function generateReferralCodePreview(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

/**
 * Validate referral code format
 * Must be exactly 8 characters, uppercase letters and numbers only
 */
export function validateReferralCodeFormat(code: string): boolean {
  const pattern = /^[A-Z2-9]{8}$/;
  return pattern.test(code);
}

/**
 * Get referral statistics for the current user
 */
export async function getReferralStats(
  userId: string,
  userType: UserType = 'client'
): Promise<ReferralStats | null> {
  try {
    const { data, error } = await supabase
      .rpc('get_referral_stats' as any, {
        p_user_id: userId,
        p_user_type: userType
      });

    if (error) {
      console.error('Error fetching referral stats:', error);
      return null;
    }

    return data as unknown as ReferralStats;
  } catch (err) {
    console.error('Exception in getReferralStats:', err);
    return null;
  }
}

/**
 * Apply a referral code for the current user
 */
export async function applyReferralCode(
  userId: string,
  referralCode: string,
  userType: UserType = 'client'
): Promise<ApplyReferralResult> {
  // Validate format first
  if (!validateReferralCodeFormat(referralCode)) {
    return {
      success: false,
      error: 'Invalid code format. Code must be 8 characters (letters and numbers only).'
    };
  }

  try {
    const { data, error } = await supabase
      .rpc('apply_referral_code' as any, {
        p_user_id: userId,
        p_referral_code: referralCode.toUpperCase(),
        p_user_type: userType
      });

    if (error) {
      console.error('Error applying referral code:', error);
      return {
        success: false,
        error: error.message || 'Failed to apply referral code.'
      };
    }

    return data as unknown as ApplyReferralResult;
  } catch (err) {
    console.error('Exception in applyReferralCode:', err);
    return {
      success: false,
      error: 'An unexpected error occurred.'
    };
  }
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
}

/**
 * Generate a shareable referral link
 */
export function generateReferralLink(referralCode: string): string {
  const baseUrl = import.meta.env.VITE_APP_PUBLIC_URL || window.location.origin;
  return `${baseUrl}/client-auth?ref=${referralCode}`;
}

/**
 * Parse referral code from referral_source field
 * Format: "REFERRAL:CODE:REFERRER_ID"
 */
export function parseReferralSource(referralSource: string | null): {
  isReferral: boolean;
  code: string | null;
  referrerId: string | null;
} {
  if (!referralSource || !referralSource.startsWith('REFERRAL:')) {
    return { isReferral: false, code: null, referrerId: null };
  }

  const parts = referralSource.split(':');
  return {
    isReferral: true,
    code: parts[1] || null,
    referrerId: parts[2] || null
  };
}
